import type { FoodLookup } from '@/domain/health/contracts'
import { foodSchema, type Food } from '@/domain/health/nutrition'

// The API's _100g fields may mean per 100 g OR per 100 ml. The UI requires the
// user to confirm the label's unit before using a result; never convert ml to g.
export function parsePackagedFood(value: unknown, barcode: string): Food {
  const data = value as { product?: { product_name_zh?: string; product_name?: string; brands?: string; nutriments?: Record<string, unknown> } }
  const p = data?.product
  if (!p) throw new Error('没有找到这个条码，可以按标签自定义')
  const n = p.nutriments
  const numeric = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v) && v >= 0
  const kcal = numeric(n?.['energy-kcal_100g']) ? n['energy-kcal_100g']
    : numeric(n?.['energy-kj_100g']) ? n['energy-kj_100g'] / 4.184 : undefined
  const name = p.product_name_zh || p.product_name
  if (!name || kcal === undefined) throw new Error('这个商品缺少名称或热量，请按标签自定义')
  return foodSchema.parse({ id: `off:${barcode}`, sourceId: barcode, name: name.slice(0, 100),
    description: (p.brands || '包装食品').slice(0, 300), origin: 'openFoodFacts',
    kcal: Math.round(kcal * 100) / 100, basis: 100, unit: 'g',
    nutrients: Object.fromEntries([['protein', 'proteins_100g'], ['carbs', 'carbohydrates_100g'], ['fat', 'fat_100g'], ['fiber', 'fiber_100g'], ['sodium', 'sodium_100g']].flatMap(([key, field]) => {
      const value = n?.[field!]
      return numeric(value) ? [[key, Math.round(value * (key === 'sodium' ? 1000 : 1) * 100) / 100]] : []
    })),
  })
}
export class OpenFoodFactsLookup implements FoodLookup {
  private cache = new Map<string, Food>()
  private lastRequest = 0
  constructor(private readonly request: typeof fetch = (input, init) => fetch(input, init)) {}
  async lookupBarcode(barcode: string): Promise<Food> {
    if (!/^\d{8,14}$/.test(barcode)) throw new Error('请输入8至14位包装条码')
    const cached = this.cache.get(barcode)
    if (cached) return structuredClone(cached)
    if (Date.now() - this.lastRequest < 5000) throw new Error('查询稍频繁，请稍等几秒再试')
    this.lastRequest = Date.now()
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), 12000)
    try {
      // Only the public barcode leaves the device, never a diary or health metric.
      const url = new URL(`https://world.openfoodfacts.org/api/v3.6/product/${barcode}.json`)
      url.searchParams.set('fields', 'product_name,product_name_zh,brands,nutriments')
      url.searchParams.set('app_name', 'CareBaby')
      const response = await this.request(url.toString(), { signal: controller.signal, credentials: 'omit', referrerPolicy: 'no-referrer' })
      if (response.status === 404) throw new Error('没有找到这个条码，可以按标签自定义')
      if (!response.ok) throw new Error('条码查询暂不可用，请稍后重试或按标签自定义')
      const food = parsePackagedFood(await response.json(), barcode)
      this.cache.set(barcode, food)
      return structuredClone(food)
    } catch (error) {
      if (error instanceof Error && !['AbortError', 'TypeError'].includes(error.name)) throw error
      throw new Error('网络连接失败，请重试或按标签自定义', { cause: error })
    } finally { clearTimeout(timer) }
  }
}
